import type { PublicRoomSummary, RoomSessionResponse } from "@secret-cabinet/contracts";
import { ArrowLeft, ArrowRight, MonitorUp, UsersRound } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { ApiError, joinRoom, roomSummary } from "./api";
import { Brand } from "./components/Brand";

interface JoinGateProps {
  code: string;
  onBack: () => void;
  onJoined: (response: RoomSessionResponse) => void;
}

export function JoinGate({ code, onBack, onJoined }: JoinGateProps) {
  const [name, setName] = useState("");
  const [summary, setSummary] = useState<PublicRoomSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void roomSummary(code)
      .then((next) => {
        if (active) {
          setSummary(next);
          setLoading(false);
        }
      })
      .catch((cause) => {
        if (active) {
          setError(cause instanceof ApiError ? cause.message : "That room could not be found.");
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [code]);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!name.trim()) {
      setError("Write the name the room should call you.");
      return;
    }
    setJoining(true);
    setError(null);
    try {
      onJoined(await joinRoom(code, { displayName: name.trim() }));
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "You could not join that room.");
      setJoining(false);
    }
  }

  return (
    <div className="gate-shell">
      <header className="gate-header">
        <Brand compact />
        <button className="icon-text-button" type="button" onClick={onBack}>
          <ArrowLeft aria-hidden="true" /> Back
        </button>
      </header>
      <main className="gate-main">
        <section className="join-sheet" aria-labelledby="join-heading">
          <p className="eyebrow">Private player entry</p>
          <div className="join-code" aria-label={`Room code ${code}`}>{code}</div>
          <h1 id="join-heading">Take your seat</h1>
          <p className="field-note">A shared screen is required for the public chamber. Keep this device for your private role and decisions.</p>
          {loading ? <p>Checking the chamber…</p> : null}
          {summary ? (
            <p className="room-capacity">
              <UsersRound aria-hidden="true" /> {summary.playerCount} of 10 seats occupied
            </p>
          ) : null}

          {summary?.canJoin ? (
            <form onSubmit={(event) => void submit(event)}>
              <label htmlFor="display-name">Your name in this game</label>
              <input
                id="display-name"
                value={name}
                onChange={(event) => setName(event.target.value.slice(0, 24))}
                autoComplete="nickname"
                enterKeyHint="go"
                placeholder="Jonathan"
                autoFocus
              />
              <p className="field-note">Your role will appear only on this device.</p>
              <button className="button button--primary button--wide" disabled={joining}>
                {joining ? "Taking seat…" : "Join room"} <ArrowRight aria-hidden="true" />
              </button>
            </form>
          ) : summary ? (
            <div className="join-closed">
              <MonitorUp aria-hidden="true" />
              <p>This game has already started or every seat is occupied.</p>
            </div>
          ) : null}
          {error ? <p className="form-error" role="alert">{error}</p> : null}
        </section>
      </main>
    </div>
  );
}
